// Header help (?) (plan 8.2.2): how to read the numbers · why something is empty · where records come from · what I may
// see (from my effective permissions) + glossary / "what is recorded" links. PC popover, phone bottom sheet.
// The mockup's first-visit tour button is not carried over.
import { CHANNELS, MISSING_REASONS } from '@aiax-hub/contracts';
import { forwardRef, useImperativeHandle, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { usePerms, useAuth, useMyRole } from '../auth/AuthContext';
import { PERM_BUNDLE_IDS, PERM_KEYS } from '../auth/perm-catalog';
import { RANK, scopeOf, type ScopeValue } from '../auth/permissions';
import { useT } from '../i18n';
import { isMobile } from '../lib/media';
import { Icon } from '../ui/Icon';
import { Modal } from '../ui/Modal';
import { Popover, focusBack } from '../ui/Popover';
import { PARTIAL_REASONS, Value } from '../ui/Value';
import { useView } from './view';

function Section({ id, title, open, children }: { id: string; title: ReactNode; open?: boolean; children: ReactNode }) {
  return (
    <details className="help-sec" id={`help-${id}`} open={open}>
      <summary>
        <span>{title}</span>
        <Icon name="chevD" className="help-sec__chev" />
      </summary>
      <div className="help-sec__body">{children}</div>
    </details>
  );
}

/** Collection paths C1~C6 with on/off when the server tells (optional until the collection-status API exists). */
export interface CollectionPathState {
  id: string;
  on: boolean;
}

export function HelpBody({ paths, onNavigate }: { paths?: readonly CollectionPathState[]; onNavigate?: () => void }) {
  const t = useT();
  const perms = usePerms();
  const { me } = useAuth();
  const myRole = useMyRole();
  const view = useView();
  // the server says which collection paths are on (C1~C6) with /auth/me
  const pathStates = paths ?? me?.org.collectionPaths;
  const bundles = PERM_BUNDLE_IDS.map((b) => {
    const keys = PERM_KEYS.filter((p) => p.bundle === b).map((p) => p.key);
    let best: ScopeValue = 'off';
    let on = 0;
    keys.forEach((k) => {
      const v = scopeOf(perms, k);
      if (v !== 'off') on++;
      if (RANK[v] > RANK[best]) best = v;
    });
    return { b, best, on, total: keys.length };
  });
  const role = myRole.name;
  const scopes = view.allowed.map((s) => t('scope.' + s)).join(' · ');
  return (
    <div
      className="help-body"
      onClick={(e) => {
        if ((e.target as Element).closest('a[href]')) onNavigate?.();
      }}
    >
      <Section id="read" title={t('help.read.title')} open>
        <ul className="help-list">
          {[1, 2, 3, 4, 5].map((i) => (
            <li key={i}>{t('help.read.' + i)}</li>
          ))}
        </ul>
      </Section>
      <Section id="empty" title={t('help.empty.title')}>
        <p className="help-lead">{t('help.empty.lead')}</p>
        <ul className="help-reasons">
          {MISSING_REASONS.map((r) => (
            <li key={r}>
              <Value state="none" reason={r} link={false} />
              <small>{t(`reason.${r}.help`)}</small>
            </li>
          ))}
        </ul>
        <p className="help-lead">
          {t('value.partial')}:{' '}
          {PARTIAL_REASONS.filter((r) => r !== 'verifying')
            .map((r) => t('partial.' + r))
            .join(' · ')}
        </p>
      </Section>
      <Section id="source" title={t('help.source.title')}>
        <p className="help-lead">{t('help.source.lead')}</p>
        <div className="help-flow" aria-hidden="true">
          <div className="help-flow__row">
            <span className="help-flow__box">
              <Icon name="terminal" />
              {t('help.flow.pc')}
            </span>
            <i>C1</i>
            <span className="help-flow__box is-core">
              <Icon name="pulse" />
              {t('help.flow.server')}
            </span>
            <i>→</i>
            <span className="help-flow__box">
              <Icon name="lock" />
              {t('help.flow.store')}
            </span>
          </div>
          <div className="help-flow__row">
            <span className="help-flow__box is-hatched">
              <Icon name="key" />
              {t('help.flow.app')}
            </span>
            <i>C3</i>
            <span className="help-flow__box is-core">
              <Icon name="route" />
              {t('help.flow.gateway')}
            </span>
            <i>→</i>
            <span className="help-flow__box">
              <Icon name="globe" />
              {t('help.flow.vendor')}
            </span>
          </div>
        </div>
        <ul className="help-paths">
          {CHANNELS.map((c) => {
            const st = pathStates?.find((p) => p.id === c);
            return (
              <li key={c} className={'help-path' + (st && !st.on ? ' is-off' : '')}>
                <b className="help-path__id">{c}</b>
                <span>
                  <strong>{t('help.source.' + c)}</strong>
                  <small>{t(`help.source.${c}.desc`)}</small>
                </span>
                {st ? <span className={'badge ' + (st.on ? 'badge--ok' : 'badge--neutral')}>{t(st.on ? 'help.source.on' : 'help.source.off')}</span> : null}
              </li>
            );
          })}
        </ul>
      </Section>
      <Section id="scope" title={t('help.scope.title')}>
        <p className="help-lead">{t('help.scope.lead', { role, scopes })}</p>
        <ul className="help-scope">
          {bundles.map((x) => (
            <li key={x.b} className="help-scope__row">
              <span>{t('permCat.' + x.b)}</span>
              <b className={`perm__cell perm__cell--${x.best}`}>{t('scopeVal.' + x.best)}</b>
              <small>{`${x.on}/${x.total}`}</small>
            </li>
          ))}
        </ul>
        <p className="src-note">
          <Icon name="lock" />
          <span>{t('help.scope.flow')}</span>
        </p>
      </Section>
      <div className="help-foot">
        <Link className="btn btn--sm" to="/glossary">
          <Icon name="book" />
          {t('help.glossary')}
        </Link>
        <Link className="btn btn--sm" to="/connect?tab=coverage">
          <Icon name="table" />
          {t('help.coverage')}
        </Link>
        <p className="help-manual">
          <Icon name="info" />
          {t('help.manual')}
        </p>
      </div>
    </div>
  );
}

export interface HelpHandle {
  open(): void;
}
/** The (?) button in the header; `ref.open()` lets the account menu open the same help. */
export const HelpButton = forwardRef<HelpHandle, { paths?: readonly CollectionPathState[] }>(function HelpButton({ paths }, ref) {
  const t = useT();
  const btnRef = useRef<HTMLButtonElement>(null);
  const [mode, setMode] = useState<null | 'pop' | 'sheet'>(null);
  const open = () => setMode(isMobile() ? 'sheet' : 'pop');
  useImperativeHandle(ref, () => ({ open }));
  const close = (returnFocus: boolean) => {
    setMode(null);
    if (returnFocus) focusBack(btnRef.current);
  };
  return (
    <>
      <button
        ref={btnRef}
        className="icon-btn help-btn"
        type="button"
        aria-haspopup="dialog"
        aria-expanded={mode === 'pop'}
        aria-label={t('help.button')}
        data-tip={t('help.button')}
        onClick={() => (mode ? close(false) : open())}
      >
        <Icon name="help" />
      </button>
      {mode === 'pop' ? (
        <Popover anchorRef={btnRef} open onClose={close} label={t('help.title')} width={420} className="popover--help" autoFocus>
          <div className="help-pop">
            <div className="popover__head">
              <div>
                <p className="popover__kicker">{t('help.kicker')}</p>
                <p className="popover__title">{t('help.title')}</p>
              </div>
              <button className="x-btn x-btn--sm" type="button" aria-label={t('common.close')} data-tip={t('common.close')} onClick={() => close(true)}>
                <Icon name="x" />
              </button>
            </div>
            <HelpBody paths={paths} onNavigate={() => close(false)} />
          </div>
        </Popover>
      ) : null}
      <Modal open={mode === 'sheet'} onClose={() => setMode(null)} kicker={t('help.kicker')} title={t('help.title')} className="sheet--panel help-sheet">
        {mode === 'sheet' ? <HelpBody paths={paths} onNavigate={() => setMode(null)} /> : null}
      </Modal>
    </>
  );
});
